import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Card, Button, PageHeader, Badge } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import {
  useAuditLogs,
  type AuditEntry,
  type AuditLogFilters,
  type AuditStatus,
} from "@/hooks/useAuditLogs";

// Phase 1 promised "every request audit-logged". The backend has done that
// since launch (`copilot_audit_entries` + GET /api/admin/audit-logs), but no
// screen ever surfaced it — so the question "what did the assistant tell
// member X?" had no answer without database access. This page is that answer.

const STATUS_OPTIONS: Array<{ value: AuditStatus | "all"; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "ok", label: "OK" },
  { value: "blocked", label: "Blocked" },
  { value: "error", label: "Error" },
];

const STATUS_BADGE_VARIANT: Record<AuditStatus, "success" | "warning" | "error"> = {
  ok: "success",
  blocked: "warning",
  error: "error",
};

const PAGE_SIZE = 50;

const truncate = (text: string | null | undefined, max = 90): string => {
  if (!text) return "—";
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max).trimEnd() + "…";
};

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : format(d, "PPpp");
};

interface EntryDrawerProps {
  entry: AuditEntry | null;
  onClose: () => void;
}

// The list endpoint returns whole rows, so the drawer needs no second fetch.
const EntryDrawer = ({ entry, onClose }: EntryDrawerProps) => {
  if (!entry) return null;

  const rows: Array<[string, React.ReactNode]> = [
    ["Time", formatWhen(entry.created_at)],
    ["Status", <Badge variant={STATUS_BADGE_VARIANT[entry.status]}>{entry.status}</Badge>],
    ["Blocked reason", entry.blocked_reason || "—"],
    ["User ID", entry.user_id || "guest"],
    ["Role", entry.role || "—"],
    ["Session", entry.session_id || "—"],
    ["Page context", entry.page_context || "—"],
    ["Provider", entry.provider || "—"],
    ["Latency", entry.latency_ms != null ? `${entry.latency_ms} ms` : "—"],
    [
      "Tools called",
      entry.tools_called?.length ? entry.tools_called.join(", ") : "none",
    ],
  ];

  return (
    <>
      <div className="dw-ov open" onClick={onClose} />
      <aside className="dw open">
        <div className="dw-h">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3>Audit entry #{entry.id}</h3>
            <p>{formatWhen(entry.created_at)}</p>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <Icon name="x" />
          </button>
        </div>

        <div className="dw-b">
          <Card>
            <h4 style={{ marginTop: 0 }}>Message preview</h4>
            <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>
              {entry.message_preview || "—"}
            </p>
            <p className="muted" style={{ marginBottom: 0 }}>
              Previews are truncated to 200 characters at write time; full
              conversation text is not stored in the audit table.
            </p>
          </Card>

          <Card>
            <h4 style={{ marginTop: 0 }}>Details</h4>
            <table className="tbl">
              <tbody>
                {rows.map(([label, value]) => (
                  <tr key={label}>
                    <td style={{ width: "40%", fontWeight: 600 }}>{label}</td>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </aside>
    </>
  );
};

const AuditLogs = () => {
  const [status, setStatus] = useState<AuditStatus | "all">("all");
  const [role, setRole] = useState("");
  const [userId, setUserId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const filters = useMemo<AuditLogFilters>(
    () => ({
      ...(status !== "all" ? { status } : {}),
      ...(role.trim() ? { role: role.trim() } : {}),
      ...(userId.trim() ? { user_id: userId.trim() } : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
      limit: PAGE_SIZE,
      offset,
    }),
    [status, role, userId, dateFrom, dateTo, offset]
  );

  const { data, isLoading, isError, error, refetch, isFetching } =
    useAuditLogs(filters);

  const entries = data?.entries ?? [];
  // No grand total from the API, so "there may be more" is inferred from a
  // full page rather than reported.
  const hasMore = entries.length === PAGE_SIZE;

  const resetFilters = () => {
    setStatus("all");
    setRole("");
    setUserId("");
    setDateFrom("");
    setDateTo("");
    setOffset(0);
  };

  return (
    <>
      <PageHeader
        label="Administration"
        title="Audit Log"
        description="Every assistant interaction, with the tools it called and whether it was blocked."
        actions={
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            <Icon name="refresh" />
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      <Card>
        <div className="row c5" style={{ alignItems: "end" }}>
          <div className="fg" style={{ marginBottom: 0 }}>
            <label className="fl" htmlFor="audit-status">Status</label>
            <select
              id="audit-status"
              className="fi"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as AuditStatus | "all");
                setOffset(0);
              }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="fg" style={{ marginBottom: 0 }}>
            <label className="fl" htmlFor="audit-role">Role</label>
            <input
              id="audit-role"
              className="fi"
              placeholder="e.g. admin"
              value={role}
              onChange={(e) => { setRole(e.target.value); setOffset(0); }}
            />
          </div>

          <div className="fg" style={{ marginBottom: 0 }}>
            <label className="fl" htmlFor="audit-user">User ID</label>
            <input
              id="audit-user"
              className="fi"
              placeholder="e.g. 1562447"
              value={userId}
              onChange={(e) => { setUserId(e.target.value); setOffset(0); }}
            />
          </div>

          <div className="fg" style={{ marginBottom: 0 }}>
            <label className="fl" htmlFor="audit-from">From</label>
            <input
              id="audit-from"
              type="date"
              className="fi"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
            />
          </div>

          <div className="fg" style={{ marginBottom: 0 }}>
            <label className="fl" htmlFor="audit-to">To</label>
            <input
              id="audit-to"
              type="date"
              className="fi"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
            />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <Button variant="ghost" onClick={resetFilters}>Reset filters</Button>
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <p className="muted">Loading audit entries…</p>
        ) : isError ? (
          <p className="muted">
            Could not load audit entries{error instanceof Error ? `: ${error.message}` : ""}.
          </p>
        ) : entries.length === 0 ? (
          <p className="muted">
            No audit entries match these filters.
          </p>
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Status</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Message</th>
                  <th>Tools</th>
                  <th>Latency</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    onClick={() => setSelected(entry)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{formatWhen(entry.created_at)}</td>
                    <td>
                      <Badge variant={STATUS_BADGE_VARIANT[entry.status]}>
                        {entry.status}
                      </Badge>
                    </td>
                    <td>{entry.user_id || "guest"}</td>
                    <td>{entry.role || "—"}</td>
                    <td>{truncate(entry.message_preview)}</td>
                    <td>{entry.tools_called?.length ?? 0}</td>
                    <td>{entry.latency_ms != null ? `${entry.latency_ms} ms` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div
              className="row"
              style={{ justifyContent: "space-between", alignItems: "center", marginTop: 12 }}
            >
              <span className="muted">
                Showing {offset + 1}–{offset + entries.length}
              </span>
              <div className="row" style={{ gap: 8 }}>
                <Button
                  variant="secondary"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  disabled={!hasMore}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      <EntryDrawer entry={selected} onClose={() => setSelected(null)} />
    </>
  );
};

export default AuditLogs;
